// Hand-rolled Turnstile loader (spec §4.1): managed widget, interaction-only,
// one single-use token per mutating call, reset after every token.
// ponytail: ~60 lines beats a wrapper package; swap for @marsidev/react-turnstile if the API grows.

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string
      reset: (id: string) => void
      remove: (id: string) => void
    }
  }
}

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

let scriptPromise: Promise<void> | undefined
function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve()
  scriptPromise ??= new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = SCRIPT
    s.async = true
    s.addEventListener('load', () => resolve(), { once: true })
    s.addEventListener('error', () => reject(new Error('turnstile_script_failed')), { once: true })
    document.head.appendChild(s)
  })
  return scriptPromise
}

let widgetId: string | undefined
let container: HTMLElement | undefined
let pending: { resolve: (t: string) => void; reject: (e: Error) => void } | undefined
let mutex: Promise<void> = Promise.resolve()

/** Mount point for the widget. Call once from the shell; it stays in the DOM. */
export function mountTurnstile(el: HTMLElement) {
  container = el
}

/**
 * Resolve one fresh token. Renders the widget lazily on first use; each token
 * is consumed by exactly one proxy call. Serialized so two sequential calls
 * (upload then run) never stomp the pending resolver.
 */
export async function getTurnstileToken(sitekey: string): Promise<string> {
  await loadScript()
  const ts = window.turnstile
  if (!ts || !container) throw new Error('turnstile_unavailable')
  if (widgetId !== undefined && !container.hasChildNodes()) widgetId = undefined

  // Serialize: a second call waits for the first token to settle before it
  // touches the shared widget. Prevents the double-reset race that left the
  // UI stuck in uploading/running with no error.
  let release!: () => void
  const next = new Promise<void>((r) => (release = r))
  const prev = mutex
  mutex = next
  await prev

  try {
    return await new Promise<string>((resolve, reject) => {
      // oxlint-disable-next-line prefer-const
      let timer: ReturnType<typeof setTimeout> | undefined
      const done = (fn: () => void) => {
        if (timer) clearTimeout(timer)
        fn()
        try {
          release()
        } catch {
          // already released
        }
      }
      pending = {
        resolve: (t) => done(() => resolve(t)),
        reject: (e) => done(() => reject(e)),
      }
      timer = setTimeout(() => {
        if (pending) {
          const p = pending
          pending = undefined
          p.reject(new Error('turnstile_timeout'))
        }
      }, 30_000)
      if (widgetId === undefined) {
        widgetId = ts.render(container!, {
          sitekey,
          appearance: 'interaction-only',
          callback: (token: string) => {
            pending?.resolve(token)
          },
          'error-callback': () => {
            pending?.reject(new Error('turnstile_failed'))
          },
          'expired-callback': () => {
            if (widgetId !== undefined) ts.reset(widgetId)
          },
        })
      } else {
        ts.reset(widgetId)
      }
    })
  } catch (e) {
    if (pending) {
      const p = pending
      pending = undefined
      try {
        p.reject(e instanceof Error ? e : new Error(String(e)))
      } catch {
        // ignore
      }
    } else {
      try {
        release()
      } catch {
        // already released via callback
      }
    }
    throw e
  }
}
